import { useEffect, useMemo, useRef, useState } from "react";
import { startUpload, upload } from "../../../../scripts/upload";
import { PartType, UploadInformationType, UploadItemProps } from "./uploadItem.interface";
import completeUploadIcon from "../../../../assets/images/fileManager/CompleteUpload.svg";
import pauseUploadIcon from "../../../../assets/images/fileManager/PauseUpload.svg";
import playUploadIcon from "../../../../assets/images/fileManager/PlayUpload.svg";
import rejectedUploadIcon from "../../../../assets/images/fileManager/RejectedUpload.svg";
import retryUploadIcon from "../../../../assets/images/fileManager/RetryUpload.svg";
import { responseValidator, advancedResponseValidator } from "../../../../scripts/api";
import { CompleteFileQueueStatus, FileUploadStatus } from "../../../../interfaces/general";
import { UnitConvertStandard, setIconFile } from "../../../../scripts/helpers";
import ProgressBar from "@ramonak/react-progress-bar";
import ReactTooltip from "react-tooltip";
import NameDisplay from "../../displayFaEn/showCorrectName/showCorrectName.index";

export default function UploadItem(props: UploadItemProps): JSX.Element {
    const [uploadInformation, setUploadInformation] = useState<UploadInformationType | undefined>(undefined);
    const [partNumber, setPartNumber] = useState<number>(1);
    const [parts, setParts] = useState<PartType[]>([]);
    const [putAbortController, setPutAbortController] = useState<any>();
    const [failedIconHover, setFailedIconHover] = useState(false);
    const [error, setError] = useState("");
    const [currentChunkLoaded, setCurrentChunkLoaded] = useState<number>(0);
    const [timerInterval, setTimerInterval] = useState<any>();
    const abortRef = useRef<any>();
    const timerRef = useRef(0);

    useEffect(() => {
        ReactTooltip.rebuild();
    });

    useEffect(() => {
        if (putAbortController) {
            abortRef.current = putAbortController;
        }
    }, [putAbortController]);

    useEffect(() => {
        if (props.data.failStatus === 413) setError("کمبود حجم");
    }, [props.data.failStatus]);

    useEffect(() => {
        return () => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (props.abort && uploadInformation && props.data.status !== FileUploadStatus.DONE) {
            const controller = new AbortController();
            controller.abort();
            if (putAbortController) putAbortController.abort();
            props.onCancel(props.data.id, uploadInformation);
            props.onChunkUpload({ id: props.data.id, total: currentChunkLoaded + parts.reduce((total, current) => total + current.size, 0) }, "-");
            setUploadInformation(undefined);
            setParts([]);
            setPartNumber(1);
        }
    }, [props.abort]);

    useEffect(() => {
        if (props.data.status === FileUploadStatus.PROGRESS && !uploadInformation && !props.abort) {
            handleStartUploadTimer();
            startUpload((data, status) => {
                if (advancedResponseValidator(data, status)) {
                    setUploadInformation(data);
                } else {
                    if (status !== 429) {
                        props.onFail(props.data.id);
                    }
                }
            }, props.data.file.size);
        }
    }, [props.data.status]);

    useEffect(() => {
        handleUpload();
    }, [uploadInformation, partNumber]);

    useEffect(() => {
        if (parts.length === uploadInformation?.signed_urls.length && uploadInformation) {
            props.onComplete({
                id: props.data.id,
                uploadInformation,
                uploadedFile: props.data,
                status: CompleteFileQueueStatus.QUEUE,
                parts
            });
            clearInterval(timerInterval);
        }
        setCurrentChunkLoaded(0);
    }, [parts]);

    useEffect(() => {
        const totalUploaded = currentChunkLoaded + parts.reduce((total, current) => total + current.size, 0);
        const currentDuration = timerRef.current / 1000;
        const speed = totalUploaded / currentDuration;
        props.transferUploadSpeed(props.data.id, isNaN(speed) ? 1 : speed);
    }, [currentChunkLoaded]);

    const handleUpload = () => {
        if (uploadInformation) {
            const resourcePart = props.data.file.slice(
                (partNumber - 1) * uploadInformation.chunk_size,
                Math.min(partNumber * uploadInformation.chunk_size, props.data.file.size)
            );
            const controller = new AbortController();
            setPutAbortController(controller);
            upload(
                (data, status, failed = false) => {
                    if (failed) {
                        controller.abort();
                        props.onFail(props.data.id);
                    } else {
                        if (responseValidator(status)) {
                            setParts([...parts, { ETag: data.etag, PartNumber: partNumber, size: resourcePart.size }]);
                            if (partNumber !== uploadInformation.signed_urls.length) {
                                setPartNumber(partNumber + 1);
                            }
                        }
                    }
                },
                uploadInformation.signed_urls[partNumber - 1],
                resourcePart,
                controller,
                (progress: { total: number; loaded: number }) => {
                    setCurrentChunkLoaded(progress.loaded);
                }
            );
        }
    };

    const handlePauseUpload = () => {
        clearInterval(timerInterval);
        props.onPause(props.data.id);
        putAbortController.abort();
    };

    const handlePlayUpload = () => {
        handleStartUploadTimer();
        props.onPlay(props.data.id);
        handleUpload();
    };

    const handleStartUploadTimer = () => {
        setTimerInterval(
            setInterval(() => {
                timerRef.current = timerRef.current + 100;
            }, 100)
        );
    };

    const percent = useMemo(() => {
        return Math.trunc(((currentChunkLoaded + parts.reduce((total, current) => total + current.size, 0)) / props.data.file.size) * 100);
    }, [currentChunkLoaded]);

    const totalUploaded = useMemo(() => {
        return currentChunkLoaded + parts.reduce((total, current) => total + current.size, 0);
    }, [currentChunkLoaded]);

    useEffect(() => {
        props.onChunkUpload({ id: props.data.id, total: currentChunkLoaded + parts.reduce((total, current) => total + current.size, 0) }, "+");
    }, [currentChunkLoaded]);

    return (
        <div className="mb-4">
            <div className="w-full flex items-center gap-4">
                <img className="w-[20px] h-[20px]" src={setIconFile(props.data.name)} alt="" />
                <div className="w-full">
                    <span className="truncate fix-text-alignment block w-[230px]">
                        <NameDisplay TextInput={props.data.name} />
                    </span>
                    <ProgressBar
                        completed={uploadInformation ? (percent <= 100 ? percent : 100) : 0}
                        className="mt-2"
                        labelSize={"0px"}
                        dir="rtl"
                        height={"5px"}
                        bgColor={"#2991FF"}
                    />
                    <div className="w-full bg-black-300 h-[8px] rounded-full relative overflow-hidden">
                        <div
                            className="absolute left-0 top-0 h-full gradient-bg rounded-full transition-all"
                            style={{
                                width: `${uploadInformation ? (percent <= 100 ? percent : 100) : 0}%`
                            }}
                        />
                    </div>
                    {uploadInformation && (
                        <p className="mt-2">
                            <span className="text-[14px] font-bold">{percent <= 100 ? percent : 100}%</span>
                            <span className="text-xs text-[#4B4B4B] mx-1 max-w-[210px] truncate">
                                (
                                {UnitConvertStandard(
                                    totalUploaded <= props.data.file.size ? totalUploaded : props.data.file.size,
                                    "fa"
                                ).value.toFixed(2)}{" "}
                                {UnitConvertStandard(totalUploaded <= props.data.file.size ? totalUploaded : props.data.file.size, "fa").unit} از{" "}
                                {UnitConvertStandard(props.data.file.size, "fa").value.toFixed(2)}{" "}
                                {UnitConvertStandard(props.data.file.size, "fa").unit})
                            </span>
                            <span className="text-xs">بارگذاری شده</span>
                        </p>
                    )}
                </div>
                <button
                    onClick={
                        props.data.status === FileUploadStatus.PROGRESS
                            ? handlePauseUpload
                            : props.data.status === FileUploadStatus.PAUSED
                              ? handlePlayUpload
                              : props.data.status === FileUploadStatus.FAILED
                                ? handlePlayUpload
                                : () => true
                    }
                    className="shrink-0"
                    onMouseOver={() => setFailedIconHover(true)}
                    onMouseLeave={() => setFailedIconHover(false)}
                    data-tip={error}
                >
                    {props.data.status === FileUploadStatus.PAUSED ? (
                        !props.completeRequest && <img className="w-[25px] h-[25px]" src={playUploadIcon} alt="play" />
                    ) : props.data.status === FileUploadStatus.DONE ? (
                        <img className="w-[25px]" src={completeUploadIcon} alt="complete" />
                    ) : props.data.status === FileUploadStatus.FAILED ? (
                        failedIconHover ? (
                            <img className="w-[25px]" src={retryUploadIcon} alt="retry" />
                        ) : (
                            <img className="w-[25px]" src={rejectedUploadIcon} alt="rejected" />
                        )
                    ) : (
                        !props.completeRequest && <img className="w-[25px]" src={pauseUploadIcon} alt="pause" />
                    )}
                </button>
            </div>
        </div>
    );
}
